import { Component, ErrorInfo, ReactNode } from 'react';
import { reportError } from '../services/api';

interface Props { children: ReactNode; fallback?: ReactNode }
interface State { hasError: boolean; errorId: string }

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorId: '' };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const errorId = crypto.randomUUID?.() ?? Date.now().toString();
    this.setState({ errorId });

    reportError({
      severity: 'high',
      category: 'frontend',
      message: error.message || 'Unhandled React render error',
      details: {
        errorId,
        name: error.name,
        stack: error.stack?.slice(0, 2000),
        componentStack: info.componentStack?.slice(0, 2000),
      },
      url: window.location.href,
    }).catch(() => {});
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{ minHeight: '100vh', background: '#F4F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 32px rgba(79,70,229,0.1)', padding: '40px', maxWidth: '440px', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#1E1B4B', marginBottom: '10px' }}>Something went wrong</h2>
            <p style={{ fontSize: '14px', color: '#64748B', marginBottom: '8px', lineHeight: 1.6 }}>
              This error has been automatically reported to our team.
            </p>
            {this.state.errorId && (
              <p style={{ fontSize: '12px', color: '#94A3B8', marginBottom: '24px' }}>
                Error ID: <code style={{ background: '#F1F5F9', padding: '2px 6px', borderRadius: '4px' }}>{this.state.errorId}</code>
              </p>
            )}
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              style={{ background: 'linear-gradient(135deg,#4F46E5,#7C3AED)', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px 24px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
